import React, {useEffect, useState} from 'react'
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {getCurrentQuarterAndYear, getQuarterDateRange, searchCounts} from '../utils'
import {webEvents} from '../../common/WebEvents'
import {useDispatch} from 'react-redux'
import {createError} from '../../store/slices/error'

type Status = 'pending' | 'in-progress' | 'in-review' | 'approved' | 'rejected'
type Type = 'full' | 'spot' | 'quarterly' | 'cycle'

type CountsByStatus = {
    [status in Status]: {full: number; spot: number; quarterly: number; cycle: number}
}

const initialCountsByStatus: CountsByStatus = {
    pending: {full: 0, spot: 0, quarterly: 0, cycle: 0},
    'in-progress': {full: 0, spot: 0, quarterly: 0, cycle: 0},
    'in-review': {full: 0, spot: 0, quarterly: 0, cycle: 0},
    approved: {full: 0, spot: 0, quarterly: 0, cycle: 0},
    rejected: {full: 0, spot: 0, quarterly: 0, cycle: 0}
}

function capitalize(str: string) {
    let words = str.split(' ')
    words = words.map(word => {
        return word.charAt(0).toUpperCase() + word.slice(1)
    })
    return words.join(' ')
}

function CountsSummaryBox() {
    const [countsByStatus, setCountsByStatus] = useState<CountsByStatus>(initialCountsByStatus)
    const [selectedQuarter, setSelectedQuarter] = useState<number>(getCurrentQuarterAndYear().quarter)
    const [selectedYear, setSelectedYear] = useState<number>(getCurrentQuarterAndYear().year)

    const currentYear = new Date().getFullYear()
    const years = Array.from({length: 3}, (_, i) => currentYear - i)
    const dispatch = useDispatch()

    const {quarterStartDate, quarterEndDate} = getQuarterDateRange(selectedQuarter, selectedYear)

    const fetchCountsByStatus = async (
        startDate: string,
        endDate: string,
        setData: React.Dispatch<React.SetStateAction<CountsByStatus>>
    ) => {
        try {
            const countsForPeriod = await searchCounts({
                requested_after: startDate,
                requested_before: endDate,
                page_size: 2000
            })

            const updatedCountsByStatus: CountsByStatus = {
                pending: {full: 0, spot: 0, quarterly: 0, cycle: 0},
                'in-progress': {full: 0, spot: 0, quarterly: 0, cycle: 0},
                'in-review': {full: 0, spot: 0, quarterly: 0, cycle: 0},
                approved: {full: 0, spot: 0, quarterly: 0, cycle: 0},
                rejected: {full: 0, spot: 0, quarterly: 0, cycle: 0}
            }

            countsForPeriod.forEach((count: any) => {
                const status = count.iteration.status as Status
                const type = count.type as Type

                if (status in updatedCountsByStatus) {
                    updatedCountsByStatus[status][type]++
                }
                if (count.type === 'cycle') {
                    updatedCountsByStatus[status].cycle++
                }
            })

            setData(updatedCountsByStatus)
        } catch (error: any) {
            webEvents.error(
                error.message,
                {'review_page': 'Could not fetch summary data'},
                error.stack
            )
            const causeMessage = error.cause?.messages?.[0] || error.messages?.[0] || 'Unknown error'

            dispatch(createError({
                type: error.type,
                message: causeMessage
            }))
        }
    }

    useEffect(() => {
        fetchCountsByStatus(quarterStartDate, quarterEndDate, setCountsByStatus)
    }, [quarterStartDate, quarterEndDate])

    const handleQuarterChange = (event: any) => {
        setSelectedQuarter(event.target.value)
    }

    const handleYearChange = (event: any) => {
        setSelectedYear(event.target.value)
    }

    return (
        <Box display="flex" flexDirection="column" alignItems="center" width="100%">
            <Accordion defaultExpanded sx={{
                width: {xs: '80%', sm: '80%', md: 600},
                mx: 'auto',
                display: 'flex',
                flexDirection: 'column'
            }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{width: '100%'}}>
                    <Box sx={{width: '100%', textAlign: 'center'}}>
                        <Typography variant="h5">Counts Summary</Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{width: '100%'}}>
                    <Grid container spacing={2} sx={{mb: 2}}>
                        <Grid item xs={6}>
                            <FormControl fullWidth>
                                <InputLabel>Quarter</InputLabel>
                                <Select value={selectedQuarter} onChange={handleQuarterChange} variant='outlined'>
                                    <MenuItem value={1}>Q1</MenuItem>
                                    <MenuItem value={2}>Q2</MenuItem>
                                    <MenuItem value={3}>Q3</MenuItem>
                                    <MenuItem value={4}>Q4</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl fullWidth>
                                <InputLabel>Year</InputLabel>
                                <Select value={selectedYear} onChange={handleYearChange} variant='outlined'>
                                    {years.map((year) => (
                                        <MenuItem key={year} value={year}>
                                            {year}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{fontWeight: 'bold'}}>Status</TableCell>
                                    <TableCell sx={{fontWeight: 'bold'}} align="right">Full</TableCell>
                                    <TableCell sx={{fontWeight: 'bold'}} align="right">Spot</TableCell>
                                    <TableCell sx={{fontWeight: 'bold'}} align="right">Quarterly</TableCell>
                                    {/*<TableCell sx={{fontWeight: 'bold'}} align="right">Cycle</TableCell>*/}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Object.keys(countsByStatus).map((status) => (
                                    <TableRow key={status}>
                                        <TableCell>{capitalize(status.replace('-', ' '))}</TableCell>
                                        <TableCell align="right">{countsByStatus[status as Status].full}</TableCell>
                                        <TableCell align="right">{countsByStatus[status as Status].spot}</TableCell>
                                        <TableCell align="right">{countsByStatus[status as Status].quarterly}</TableCell>
                                        {/*<TableCell align="right">{countsByStatus[status as Status].cycle}</TableCell>*/}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </AccordionDetails>
            </Accordion>
        </Box>
    )
}

export default CountsSummaryBox
